import React, { useEffect, useState } from "react";
import DebounceSmallSearchInput from "../../Constant/DebounceSmallSearchInput";
import GlobalSearchableDropdown from "../../Constant/SearchableDropdown";
import { TextField, MenuItem } from "@mui/material";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { MdDelete } from "react-icons/md";
import { IoMdAdd } from "react-icons/io";
import { FaCheck } from "react-icons/fa";
import { LuUserRoundSearch } from "react-icons/lu";
import { GrUserWorker } from "react-icons/gr";

import {
  formatToIndianNumber,
  generateMonthsFrom2025,
  getPreviousMonth,
  toInitials,
} from "../../../utilities";

import {
  createDoorStepEmployeeSalaryAccountingApi,
  getDoorStepEmployeeListApi,
  getDoorStepEmployeeSalaryAccountingApi,
} from "../../../services/DoorStepEmployeeService";
import { CancelRequest } from "../../../services/AxiosInstance";
const allowanceList = [
  { label: "Customer Referral", value: "customer_referral" },
  { label: "Employee Referral", value: "employee_referral" },
  { label: "Other Referral", value: "other" },
];
const mappEdKey={
customer_referral:'Customer Referral',
employee_referral:'Employee Referral',
other:'Other',
}
const AccountingForm = ({
  selectedEmployee,
  selectedMonth,
  previousAccountingData,
  accountingData,
  onAfterSubmit,
  isEdit = false,
  setView,
}) => {
  const [form, setForm] = useState({
    car: "",
    bike: "",
    leave: "",
    penalty: "",
    advance: "",
    others: "",
  });
  const [allowance, setAllowance] = useState([{ name: "", price: "" }]);
  const [allowanceError, setAllowanceError] = useState([]);
  const [isPrevCheck, setIsPrevCheck] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  
  useEffect(()=>{
    if(isEdit){
       setForm({
        car:Number(accountingData?.categories?.car_count)||'',
        bike:Number(accountingData?.categories?.bike_count)||'',
        leave: Number(accountingData?.leave) || "",
        penalty: Number(accountingData?.penalty) || "",
        advance: Number(accountingData?.advance_deduction) || "",
        others: Number(accountingData?.other_deduction) || "",
      });
      setAllowance(
        accountingData?.allowances?.map((item) => ({
          name: item?.type,
          price: Number(item?.amount),
        })))
    }
  },[isEdit])
  // ----- Handle Auto-fill previous month -----
  const handleCheck = (isCheck) => {
    setIsPrevCheck(isCheck);
    if (isCheck && previousAccountingData) {
      setForm({
        car:Number(previousAccountingData?.categories?.car_count)||'',
        bike:Number(previousAccountingData?.categories?.bike_count)||'',
        leave: Number(previousAccountingData?.leave) || "",
        penalty: Number(previousAccountingData?.penalty) || "",
        advance: Number(previousAccountingData?.advance_deduction) || "",
        others: Number(previousAccountingData?.other_deduction) || "",
      });
      setAllowance(
        previousAccountingData?.allowances?.map((item) => ({
          name: item?.type,
          price: Number(item?.amount),
        }))
      );
    } else {
      resetForm();
    }
  };

  const resetForm = () => {
    setForm({
      car: "",
      bike: "",
      leave: "",
      penalty: "",
      advance: "",
      others: "",
    });
    setAllowance([{ name: "", price: "" }]);
    setAllowanceError([]);
  };

  // ----- Generic Numeric Input Handler -----
  const handleFormChange = (name, value) => {
    if (/^\d*$/.test(value)) setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ----- Allowance Handlers -----
  const handleAddAllowance = () => {
    if (allowance.length < allowanceList.length) {
      setAllowance([...allowance, { name: "", price: "" }]);
    }
  };

  const handleDeleteAllowance = (index) => {
    setAllowance(allowance.filter((_, i) => i !== index));
  };

  const handleChangeAllowance = (index, field, value) => {
    const updated = [...allowance];
    updated[index][field] = value;
    setAllowance(updated);

    if (value && allowanceError[index]?.[field]) {
      const errors = [...allowanceError];
      errors[index][field] = "";
      setAllowanceError(errors);
    }
  };

  // ----- Submit Handling -----
  const createPayload = () => {
    const payload = {
      categories: { car: form.car || 0, bike: form.bike || 0 },
      leave: form.leave || 0,
      penalty: form.penalty || 0,
      advance_deduction: form.advance || 0,
      other_deduction: form.others || 0,
      allowances: allowance
        .filter((a) => a.name && a.price)
        .map((item) => ({ type: item.name, amount: item.price || 0 })),
    };
    return payload;
  };

  const handleSubmit = () => {
    const errors = allowance.map((item) => {
      const err = {};
      if ((item.name || item.price) && !item.name) err.name = "Required";
      if ((item.name || item.price) && !item.price) err.price = "Required";
      return err;
    });
    setAllowanceError(errors);

    if (errors.every((e) => Object.keys(e).length === 0)) {
      setSubmitLoading(true);
      createDoorStepEmployeeSalaryAccountingApi(
        selectedEmployee.id,
        { year_month: selectedMonth.value },
        createPayload()
      )
        .then(() => {
          resetForm();
          toast.success("Updated successfully");
          onAfterSubmit();
        })
        .catch((err) =>
          toast.error(err?.response?.data?.message || "Try again later")
        )
        .finally(() => setSubmitLoading(false));
    }
  };

  return (
    <div className="card rounded-3 overflow-hidden">
      {/* Auto-fill Warning */}
      <div
        className="alert alert-warning border-0 border-bottom d-flex flex-column mb-4 rounded-0"
        role="alert"
      >
        <div className="fw-semibold mb-1">⚠️ Attention Required</div>
        <div className="text-dark mb-2" style={{ fontSize: "0.9rem" }}>
          You did not submit the monthly report for this employee. Please fill
          in the required fields below. If you prefer, you can quickly auto-fill
          the fields with last month’s data by checking the option.
        </div>

        <div className="form-check">
          <input
            className="form-check-input cursor-pointer"
            type="checkbox"
            id="autofillCheckbox"
            style={{ height: "18px", width: "18px" }}
            disabled={!previousAccountingData}
            checked={isPrevCheck}
            onChange={(e) => handleCheck(e.target.checked)}
          />
          <label
            className="form-check-label fw-medium text-primary fst-italic cursor-pointer"
            htmlFor="autofillCheckbox"
            style={{ paddingLeft: "0.5rem" }}
          >
            Auto-fill with previous month’s data
          </label>
        </div>
      </div>

      {/* Form Inputs */}
      <div className="px-3">
        <div className="row g-2">
          <div className="col-6">
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={`${selectedEmployee?.apartment_type || "Total"} Cars`}
              value={form.car}
              onChange={(e) => handleFormChange("car", e.target.value)}
            />
          </div>
          <div className="col-6">
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={`${selectedEmployee?.apartment_type || "Total"} Bikes`}
              value={form.bike}
              onChange={(e) => handleFormChange("bike", e.target.value)}
            />
          </div>
        </div>

        {/* Allowances */}
        <div className="bg-lighter rounded-3 mt-3 p-2">
          <div className="d-flex justify-content-between py-1 mb-1">
            <h5 className="m-0">Allowance</h5>
            <button className="btn btn-primary" onClick={handleAddAllowance} style={{padding:'0.4rem 0.5rem'}}>
              <IoMdAdd className="me-1" /> Allowance
            </button>
          </div>
          <div className="d-flex flex-column gap-3">
            {allowance.map((alw, i) => (
              <div className="row g-2" key={i}>
                <div className="col">
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    select
                    label="Allowance Type"
                    value={alw.name}
                    error={Boolean(allowanceError[i]?.name)}
                    helperText={allowanceError[i]?.name || ""}
                    onChange={(e) =>
                      handleChangeAllowance(i, "name", e.target.value)
                    }
                  >
                    {allowanceList
                      .filter(
                        (a) =>
                          !allowance.some(
                            (al, idx) => al.name === a.value && idx !== i
                          )
                      )
                      .map((item) => (
                        <MenuItem key={item.value} value={item.value}>
                          {item.label}
                        </MenuItem>
                      ))}
                  </TextField>
                </div>
                <div className="col">
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Price"
                    value={alw.price}
                    error={Boolean(allowanceError[i]?.price)}
                    helperText={allowanceError[i]?.price || ""}
                    onChange={(e) =>
                      handleChangeAllowance(i, "price", e.target.value)
                    }
                  />
                </div>
                {allowance.length > 1 && (
                  <div className="col-auto">
                    <button
                      className="btn btn-outline-secondary"
                      onClick={() => handleDeleteAllowance(i)}
                      style={{
                        padding: "0.5rem",
                        borderRadius: "8px",
                      }}
                    >
                      <MdDelete size={18} />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Deductions */}
        <div className="row g-2 mt-2">
          {["leave", "penalty", "advance", "others"].map((field, idx) => (
            <div className="col-6 col-md-4" key={idx}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label={field.charAt(0).toUpperCase() + field.slice(1)}
                value={form[field]}
                onChange={(e) => handleFormChange(field, e.target.value)}
              />
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="d-flex justify-content-center gap-2 my-4">
          <button
            className="btn btn-outline-dark"
            onClick={isEdit ? () => setView("accountingdetails") : resetForm}
            disabled={submitLoading}
          >
            {isEdit ? "Back" : "Reset"}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={submitLoading}
          >
            {submitLoading && <Spinner animation="border" size="sm" />} Submit
          </button>
        </div>
      </div>
    </div>
  );
};

const DoorStepSalaryCalculation = () => {
  const [selectedMonth, setSelectedMonth] = useState(
    generateMonthsFrom2025()[0]
  );
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [accountingData, setAccountingData] = useState(null);
  const [previousAccountingData, setPreviousAccountingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [accountingLoading, setAccountingLoading] = useState(false);
  const [view, setView] = useState("accountingdetails");
  const [param, setParam] = useState({
    page: "1",
    page_size: "100",
    query: "",
  });

  useEffect(() => getEmployees(), [param]);
  useEffect(() => {
    if (selectedEmployee && selectedMonth) {
      getAccountingData();
      getPreviousMonthAccountingData();
      setView("accountingdetails");
    }
  }, [selectedEmployee, selectedMonth]);

  const getEmployees = () => {
    setLoading(true);
    getDoorStepEmployeeListApi(param)
      .then((res) => setEmployees(res?.data?.results || []))
      .catch(() => toast.error("Try again later"))
      .finally(() => setLoading(false));
  };

  const getAccountingData = () => {
    setAccountingLoading(true);
    getDoorStepEmployeeSalaryAccountingApi(selectedEmployee?.id || "", {
      year_month: selectedMonth?.value,
    })
      .then((res) => {
        setAccountingData(
          res?.data?.results && Object.keys(res.data.results).length > 0
            ? res.data.results
            : null
        );
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      })
      .finally(() => {
        setAccountingLoading(false);
      });
  };

  const getPreviousMonthAccountingData = () => {
    const prev_month = getPreviousMonth(selectedMonth?.value);
    getDoorStepEmployeeSalaryAccountingApi(selectedEmployee?.id || "", {
      year_month: prev_month,
    })
      .then((res) => {
        setPreviousAccountingData(
          res?.data?.results && Object.keys(res.data.results).length > 0
            ? res.data.results
            : null
        );
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Try again later"
        );
      });
  };

  const handleSearchChange = (value) => {
    if (value.length) CancelRequest();
    setParam((prev) => ({ ...prev, query: value, page: 1 }));
  };
  const handleAfterSubmitForm = () => {
    getAccountingData();
    setView("accountingdetails");
  };
  return (
    <div className="container-fluid p-0">
      <div className="row g-3">
        {/* Employee List */}
        <div className="col-md-4">
          <div className="card rounded-3 p-3">
            <div className="d-flex justify-content-between flex-wrap gap-2">
              <h5>Door Step Employees</h5>
              <GlobalSearchableDropdown
                options={generateMonthsFrom2025()}
                selected={selectedMonth}
                onSelect={setSelectedMonth}
                placeholder="Select Month"
                isClearable={false}
              />
            </div>
            <div className="mt-3">
              <DebounceSmallSearchInput
                handleSearchChange={handleSearchChange}
              />
            </div>
            {loading ? (
              <div className="text-center my-4">
                <Spinner animation="border"/>
              </div>
            ) : (
              <div
                style={{ maxHeight: "62vh", overflowY: "auto" }}
                className="mt-4"
              >
                {employees.map((emp) => (
                  <div
                    key={emp.id}
                    className={`card rounded-3 border mb-2 cursor-pointer ${
                      selectedEmployee?.id === emp.id
                        ? "text-bg-info-soft border-info"
                        : "bg-lighter border-light"
                    }`}
                    onClick={() => setSelectedEmployee(emp)}
                  >
                    <div className="px-2 py-3 d-flex align-items-center">
                      <div
                        className={`rounded-circle d-flex justify-content-center align-items-center overflow-hidden ${
                          selectedEmployee?.id === emp.id
                            ? "bg-info text-white"
                            : "bg-light text-dark"
                        }`}
                        style={{
                          width: 45,
                          height: 45,
                          fontWeight: 500,
                          fontSize: "1rem",
                        }}
                      >
                        {toInitials(emp.name)}
                      </div>
                      <div className="ms-2">
                        <h6 className="text-darker m-0">{emp.name}</h6>
                        <small className="text-dark">
                          {emp.designation || "-"}
                        </small>
                      </div>
                      {selectedEmployee?.id === emp.id && (
                        <FaCheck className="text-info ms-auto" />
                      )}
                    </div>
                  </div>
                ))}
                <div
                  className="fw-medium text-dark mt-2 text-center"
                  style={{ fontSize: "0.85rem" }}
                >
                  Total Employees: {employees.length}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Accounting Section */}
        <div className="col-md-8">
          {accountingLoading ? (
            <div className="text-center my-5">
              <Spinner animation="border" />
            </div>
          ) : selectedEmployee ? (
            accountingData && view === "accountingdetails" ? (
              <div className="card rounded-3">
                <div className="p-2 d-flex justify-content-between align-items-center border-bottom">
                  <div className="d-flex gap-1">
                    <span className="rounded-2 text-bg-success-soft align-self-center p-2 py-1">
                      <GrUserWorker />
                    </span>
                    <div>
                      <h6 className="m-0">{selectedEmployee?.name || "-"}</h6>
                      <small className="text-dark">
                        {selectedEmployee?.designation || "Designation"} •{" "}
                        {selectedEmployee?.apartment_type || "-"}
                      </small>
                    </div>
                  </div>
                  <button
                    className="btn stylish-transparent-btn"
                    onClick={() => setView("accountingForm")}
                  >
                    Edit Salary
                  </button>
                </div>
                <div className="card-body p-2">
                  <div className="alert alert-danger mb-2 mx-1">
                    <p
                      style={{
                        fontFamily: "monospace",
                        fontSize: "0.85rem",
                      }}
                    >
                      Employee’s monthly salary summary has already been entered
                      into the system. If you wish to make changes, please click
                      the <span className="fw-semibold text-darker">Edit</span>{" "}
                      button above.
                    </p>
                  </div>
                  <div className="table-responsive">
                    <table className="table table-sm table-borderless align-middle mb-0 fs-6">
                      <tbody>
                        <tr>
                          <td>
                            {selectedEmployee.apartment_type || "Total"} Car:
                          </td>
                          <td className="text-end">{accountingData?.categories?.car_count||0}</td>
                        </tr>
                        <tr>
                          <td>
                            {selectedEmployee.apartment_type || "Total"} Bike:
                          </td>
                          <td className="text-end">{accountingData?.categories?.bike_count||0}</td>
                        </tr>
                        {accountingData.allowances?.map((item, idx) => (
                          <tr key={idx}>
                            <td className="text-success">+ {mappEdKey[item.type]||'Allowance'}:</td>
                            <td className="text-end text-success">
                              ₹{formatToIndianNumber(item.amount)}
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td className="text-danger">– Leave</td>
                          <td className="text-end text-danger">
                            ₹{formatToIndianNumber(accountingData.leave)}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-danger">– Penalty</td>
                          <td className="text-end text-danger">
                            ₹{formatToIndianNumber(accountingData.penalty)}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-danger">– Advance</td>
                          <td className="text-end text-danger">
                            ₹
                            {formatToIndianNumber(
                              accountingData.advance_deduction
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-danger">– Other</td>
                          <td className="text-end text-danger">
                            ₹
                            {formatToIndianNumber(
                              accountingData.other_deduction
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                {accountingData.updated_at && (
                  <div className="p-2 border-top text-center small text-dark">
                    <span className="fst-italic">
                      Last updated on:{" "}
                      {format(
                        accountingData.updated_at,
                        "dd MMM yyyy, hh:mm a"
                      )}
                    </span>
                  </div>
                )}
              </div>
            ) : (
              <AccountingForm
                selectedEmployee={selectedEmployee}
                selectedMonth={selectedMonth}
                previousAccountingData={previousAccountingData}
                onAfterSubmit={handleAfterSubmitForm}
                isEdit={Boolean(accountingData)}
                setView={setView}
                accountingData={accountingData}
              />
            )
          ) : (
            <div
              style={{ minHeight: "30vh" }}
              className="card rounded-3 p-4 text-bg-warning-soft border border-warning d-flex flex-column align-items-center justify-content-center gap-2 border-warning"
            >
              <LuUserRoundSearch className="text-dark" size={40} />
              <p className="mb-0 fw-medium text-dark">
                Kindly select an employee before proceeding with salary
                calculation for this month.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoorStepSalaryCalculation;
